import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('mtss_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// API functions
export const apiService = {
  // Dashboard
  getDashboard: (schoolId) => api.get('/dashboard', { params: { school_id: schoolId } }),

  // Terms & Conditions
  getCurrentTerms: () => api.get('/terms/current'),
  getTermsStatus: () => api.get('/terms/status'),
  acceptTerms: (data) => api.post('/terms/accept', data),

  // Schools
  getSchools: () => api.get('/schools'),
  createSchool: (data) => api.post('/schools', data),
  updateSchool: (id, data) => api.put(`/schools/${id}`, data),
  deleteSchool: (id) => api.delete(`/schools/${id}`),
  
  // School Subscription Management (Super Admin)
  updateSchoolAccount: (schoolId, data) => api.put(`/schools/${schoolId}/account`, data),
  backfillSchoolAccounts: () => api.post('/schools/backfill-accounts'),

  // Students
  getStudents: (params) => api.get('/students', { params }),
  getStudent: (id) => api.get(`/students/${id}`),
  createStudent: (data) => api.post('/students', data),
  updateStudent: (id, data) => api.put(`/students/${id}`, data),
  deleteStudent: (id) => api.delete(`/students/${id}`),

  // Interventions
  getInterventions: (params) => api.get('/interventions', { params }),
  getIntervention: (id) => api.get(`/interventions/${id}`),
  createIntervention: (data) => api.post('/interventions', data),
  updateIntervention: (id, data) => api.put(`/interventions/${id}`, data),
  deleteIntervention: (id) => api.delete(`/interventions/${id}`),

  // Progress Data
  getProgress: (params) => api.get('/progress', { params }),
  getProgressItem: (id) => api.get(`/progress/${id}`),
  createProgress: (data) => api.post('/progress', data),
  updateProgress: (id, data) => api.put(`/progress/${id}`, data),
  deleteProgress: (id) => api.delete(`/progress/${id}`),

  // Tier History
  getTierHistory: (studentId) => api.get(`/tier-history/${studentId}`),

  // Reports
  getStudentSummaryReport: (studentId) => api.get(`/reports/student-summary/${studentId}`),

  // Settings
  getSettings: () => api.get('/settings'),
  updateSettings: (data) => api.put('/settings', data),
  
  // User Profile (structured account info)
  getUserProfile: () => api.get('/user/profile'),

  // Quick Entry
  getStudentsForQuickEntry: () => api.get('/quick-entry/students'),
  createBulkProgress: (entries) => api.post('/progress/bulk', { entries }),

  // Exports - returns blob
  exportStudents: (params) => api.get('/export/students', { params, responseType: 'blob' }),
  exportInterventions: (params) => api.get('/export/interventions', { params, responseType: 'blob' }),
  exportProgress: (params) => api.get('/export/progress', { params, responseType: 'blob' }),
  exportStudentFull: (studentId) => api.get(`/export/student/${studentId}`, { responseType: 'blob' }),

  // User Management (Admin only)
  getUsers: () => api.get('/users'),
  createUser: (data) => api.post('/users/create', data),
  updateUserRole: (userId, role, schoolId) => api.put(`/users/${userId}/role`, { role, school_id: schoolId }),
  deleteUser: (userId) => api.delete(`/users/${userId}`),
  resetUserPassword: (userId) => api.post(`/users/${userId}/reset-password`),

  // Auth - Password Change
  changePassword: (currentPassword, newPassword) => api.post('/auth/change-password', {
    current_password: currentPassword,
    new_password: newPassword
  }),

  // Auth - Forgot Password / Reset Password
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  verifyResetToken: (token) => api.get('/auth/verify-reset-token', { params: { token } }),
  resetPassword: (token, newPassword) => api.post('/auth/reset-password', {
    token,
    new_password: newPassword
  }),

  // CSV Import
  previewStudentImport: (file, schoolId = null) => {
    const formData = new FormData();
    formData.append('file', file);
    let url = '/import/students/preview';
    if (schoolId) {
      url += `?school_id=${schoolId}`;
    }
    return api.post(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  confirmStudentImport: (file, skipDuplicates = true, schoolId = null) => {
    const formData = new FormData();
    formData.append('file', file);
    let url = `/import/students/confirm?skip_duplicates=${skipDuplicates}`;
    if (schoolId) {
      url += `&school_id=${schoolId}`;
    }
    return api.post(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getImportTemplate: () => api.get('/import/students/template', { responseType: 'blob' }),

  // AI Features
  generateAISummary: async (studentId) => {
    const response = await api.post('/ai/draft-summary', { student_id: studentId });
    return response.data;
  },

  // Student Notes
  getStudentNotes: (studentId) => api.get('/student-notes', { params: { student_id: studentId } }),
  createStudentNote: (data) => api.post('/student-notes', data),
  updateStudentNote: (id, data) => api.put(`/student-notes/${id}`, data),
  deleteStudentNote: (id) => api.delete(`/student-notes/${id}`),

  // Student Context Profile
  getStudentContextProfile: (studentId) => api.get(`/students/${studentId}/context-profile`),
  updateStudentContextProfile: (studentId, data) => api.put(`/students/${studentId}/context-profile`, data),

  // Staff Contacts (School Staff Directory)
  getStaffContacts: (params) => api.get('/staff-contacts', { params }),
  getStaffContact: (id) => api.get(`/staff-contacts/${id}`),
  createStaffContact: (data) => api.post('/staff-contacts', data),
  updateStaffContact: (id, data) => api.put(`/staff-contacts/${id}`, data),
  deleteStaffContact: (id) => api.delete(`/staff-contacts/${id}`),
  findOrCreateStaffContact: (data) => api.post('/staff-contacts/find-or-create', data),
  inferStaffNameFromEmail: (email) => api.get('/staff-contacts/infer-name', { params: { email } }),

  // Eligible Intervention Providers (Users + Staff Contacts marked as providers)
  getEligibleInterventionProviders: (schoolId) => api.get('/eligible-intervention-providers', { params: { school_id: schoolId } }),

  // Feature Flags
  getFeatureFlags: () => api.get('/feature-flags'),
  updateFeatureFlag: (flagName, value, schoolId = null) => api.put('/feature-flags', {
    flag_name: flagName,
    value,
    school_id: schoolId
  }),

  // Assessments Module
  // Templates
  getAssessmentTemplates: (params) => api.get('/assessments/templates', { params }),
  getAssessmentTemplate: (id) => api.get(`/assessments/templates/${id}`),
  createAssessmentTemplate: (data) => api.post('/assessments/templates', data),
  updateAssessmentTemplate: (id, data) => api.put(`/assessments/templates/${id}`, data),
  deleteAssessmentTemplate: (id) => api.delete(`/assessments/templates/${id}`),
  
  // Administrations
  getAssessmentAdministrations: (params) => api.get('/assessments/administrations', { params }),
  getAssessmentAdministration: (id) => api.get(`/assessments/administrations/${id}`),
  createAssessmentAdministration: (data) => api.post('/assessments/administrations', data),
  updateAssessmentAdministration: (id, data) => api.put(`/assessments/administrations/${id}`, data),
  
  // Assessments Summary & Workflow
  getAssessmentSummary: (schoolId) => api.get('/assessments/summary', { params: { school_id: schoolId } }),
  createInterventionFromAssessment: (data) => api.post('/assessments/create-intervention-from-result', data),
  
  // Pilot Interest
  submitPilotInterest: () => api.post('/assessments/pilot-interest')
};

// Helper to trigger download
export const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

export default api;
